/**
 * Centralized SEO configuration.
 * Single source of truth for canonical site URL, default OG image,
 * supported locales, and brand-level constants used by JSON-LD schemas.
 */

export const SITE_URL = 'https://indiefy.net';

export const SITE_NAME = 'Indiefy';

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

export const DEFAULT_OG_IMAGE_WIDTH = 1200;

export const DEFAULT_OG_IMAGE_HEIGHT = 630;

export const TWITTER_HANDLE = '@indiefy';

export const SUPPORTED_LOCALES = ['en', 'es', 'pt', 'tr', 'hi', 'fr'];

export const DEFAULT_LOCALE = 'en';

export const SOCIAL_PROFILES = [
  'https://facebook.com/indiefy',
  'https://twitter.com/indiefy',
  'https://instagram.com/indiefy',
  'https://youtube.com/indiefy',
];

/**
 * Brand JSON-LD building blocks. Functions take the i18n `t()` function
 * so descriptions can be localized at call time.
 */
export const buildOrganizationSchema = (t, { logo } = {}) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: logo || `${SITE_URL}/og-image.jpg`,
  description: t('landing.page_description'),
  sameAs: SOCIAL_PROFILES,
});

export const buildWebSiteSchema = (t) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: t('landing.page_description'),
  inLanguage: SUPPORTED_LOCALES,
});

export const buildBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url.startsWith('http') ? item.url : `${SITE_URL}${item.url}`,
  })),
});

/**
 * Public routes used by the sitemap generator and the post-build
 * prerender script. Single source of truth for indexable URLs — keep
 * this list in sync with `router/index.js` when public routes change.
 */
export const PUBLIC_ROUTES = [
  { path: '/', changefreq: 'weekly', priority: 1.0 },
  { path: '/music-distribution-and-monetization', changefreq: 'monthly', priority: 0.9 },
  { path: '/free-music-distribution', changefreq: 'monthly', priority: 0.9 },
  { path: '/distribute', changefreq: 'weekly', priority: 0.95 },
  { path: '/distribution-glossary', changefreq: 'monthly', priority: 0.8 },
  { path: '/artists-catalog', changefreq: 'weekly', priority: 0.7 },
  { path: '/promotion', changefreq: 'monthly', priority: 0.8 },
  { path: '/cover-editor', changefreq: 'monthly', priority: 0.7 },
  { path: '/mastering', changefreq: 'monthly', priority: 0.8 },
  { path: '/upgrade', changefreq: 'monthly', priority: 0.7 },
  { path: '/playlists', changefreq: 'weekly', priority: 0.6 },
  { path: '/blog', changefreq: 'daily', priority: 0.8 },
  { path: '/terms-and-conditions', changefreq: 'yearly', priority: 0.3 },
  { path: '/privacy-policy', changefreq: 'yearly', priority: 0.3 },
  { path: '/cookie-policy', changefreq: 'yearly', priority: 0.3 },
];

// Utility/alias URLs still need fresh HTML (noindex or a canonical to the
// preferred URL), even though they must not be advertised in the sitemap.
export const PRERENDER_ROUTES = [
  ...PUBLIC_ROUTES,
  ...['/auth/login', '/auth/signup', '/auth/forgot-password', '/pro', '/pro/yearly']
    .map(path => ({ path })),
];
